import React, { useState, useEffect } from "react";
import TrainingAPI from "../services/TrainingAPI";
import LoadingSpinner from "../common/LoadingSpinner";
import "./css/Training.css";
import "./css/TrainingList.css";

/**
 * Available training categories.
 * These should match the categories stored in the trainings.category field (comma-separated).
 * Categories are stored and displayed with proper title case (e.g., "Social Engineering").
 */
const TRAINING_CATEGORIES = [
  "All",
  "Phishing",
  "Email Security",
  "Online Safety",
  "Password Security",
  "Privacy Protection",
  "Social Engineering",
  "Phone Security",
  "Device Security",
  "Financial Protection",
  "Financial Scams",
  "Romance Scams",
  "General",
];

/**
 * Formats a category string to display-ready format.
 * Converts hyphenated names like "social-engineering" to "Social Engineering".
 * If already in display format, returns as-is.
 */
const formatCategoryForDisplay = (category) => {
  if (!category) return "General";
  // If already has spaces, assume it's display-ready
  if (category.includes(" ")) return category;
  // Convert hyphenated to title case: "social-engineering" -> "Social Engineering"
  return category
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * TrainingList - Displays all available trainings with user progress
 *
 * Features:
 * - Grid view of training cards
 * - Filter by category dropdown (like MoreBlogs.js)
 * - Search functionality (local filtering)
 * - User progress indicators
 * - Featured trainings highlighted
 */
const TrainingList = ({ onSelectTraining }) => {
  const [trainings, setTrainings] = useState([]);
  const [allTrainings, setAllTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categoryCounts, setCategoryCounts] = useState({});
  const [statusTab, setStatusTab] = useState("continue"); // "continue" or "completed"

  useEffect(() => {
    fetchData();
  }, []);

  // Listen for search events from the header
  useEffect(() => {
    const handleSearchEvent = (event) => {
      const query = event.detail;
      console.log("[TrainingList] Search event received, query:", query);

      if (!query || !query.trim()) {
        console.log("[TrainingList] Empty query, resetting to filtered view");
        handleCategoryChange(selectedCategory);
        return;
      }

      // Search locally from allTrainings
      const lowerQuery = query.toLowerCase();
      const filtered = allTrainings.filter(
        (t) =>
          t.title?.toLowerCase().includes(lowerQuery) ||
          t.description?.toLowerCase().includes(lowerQuery) ||
          t.summary?.toLowerCase().includes(lowerQuery) ||
          t.category?.toLowerCase().includes(lowerQuery),
      );
      console.log("[TrainingList] Local search results:", filtered.length);
      setTrainings(filtered);
    };

    // Listen for search reset event (when user clicks outside search)
    const handleSearchReset = () => {
      console.log(
        "[TrainingList] Search reset, reverting to category:",
        selectedCategory,
      );
      if (selectedCategory === "All") {
        setTrainings(allTrainings);
      } else {
        const filtered = allTrainings.filter((t) =>
          trainingHasCategory(t, selectedCategory),
        );
        setTrainings(filtered);
      }
    };

    window.addEventListener("trainingSearch", handleSearchEvent);
    window.addEventListener("trainingSearchReset", handleSearchReset);
    return () => {
      window.removeEventListener("trainingSearch", handleSearchEvent);
      window.removeEventListener("trainingSearchReset", handleSearchReset);
    };
  }, [allTrainings, selectedCategory]);

  /**
   * Check if a training belongs to a category.
   * Training.category is a comma-separated string (e.g., "Phishing,Email Security,Online Safety")
   * Handles both display format ("Social Engineering") and hyphenated format ("social-engineering")
   */
  const trainingHasCategory = (training, category) => {
    if (!training.category) return false;
    // Split by comma and normalize each category for comparison
    const trainingCategories = training.category
      .split(",")
      .map((c) => formatCategoryForDisplay(c.trim()).toLowerCase());
    return trainingCategories.includes(category.toLowerCase());
  };

  /**
   * Calculate how many trainings belong to each category
   */
  const calculateCategoryCounts = (trainings) => {
    const counts = { All: trainings.length };
    TRAINING_CATEGORIES.slice(1).forEach((category) => {
      counts[category] = trainings.filter((t) =>
        trainingHasCategory(t, category),
      ).length;
    });
    return counts;
  };

  /**
   * Get count of trainings by completion status
   */
  const getStatusCounts = (trainingsToCount) => {
    const completed = trainingsToCount.filter(
      (t) => t.userStatus === "COMPLETED",
    ).length;
    const continueCount = trainingsToCount.length - completed;
    return { continue: continueCount, completed };
  };

  /**
   * Filter trainings by status tab
   * "continue" = NOT_STARTED + IN_PROGRESS (sorted: in-progress first)
   * "completed" = COMPLETED only
   */
  const filterByStatus = (trainingsToFilter, tab) => {
    if (tab === "completed") {
      return trainingsToFilter.filter((t) => t.userStatus === "COMPLETED");
    }
    // "continue" tab - show not started and in progress
    const filtered = trainingsToFilter.filter(
      (t) => t.userStatus !== "COMPLETED",
    );
    // Sort: IN_PROGRESS first, then NOT_STARTED
    return filtered.sort((a, b) => {
      if (a.userStatus === "IN_PROGRESS" && b.userStatus !== "IN_PROGRESS")
        return -1;
      if (a.userStatus !== "IN_PROGRESS" && b.userStatus === "IN_PROGRESS")
        return 1;
      return 0;
    });
  };

  const fetchData = async () => {
    console.log("[TrainingList] fetchData called");
    try {
      setLoading(true);
      setError(null);

      const trainingsRes = await TrainingAPI.getAllTrainings();
      console.log("[TrainingList] Trainings API response:", trainingsRes);

      if (trainingsRes.success) {
        const fetchedTrainings = trainingsRes.trainings || [];
        console.log(
          "[TrainingList] Fetched trainings count:",
          fetchedTrainings.length,
        );
        setTrainings(fetchedTrainings);
        setAllTrainings(fetchedTrainings);
        setCategoryCounts(calculateCategoryCounts(fetchedTrainings));
      }
    } catch (err) {
      console.error("[TrainingList] Error fetching data:", err);
      setError("Failed to load trainings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle category filter change from dropdown
   */
  const handleCategoryChange = (category) => {
    console.log("[TrainingList] Category changed to:", category);
    setSelectedCategory(category);

    // Notify Training.js to collapse the search input
    window.dispatchEvent(new CustomEvent("trainingCategorySelected"));

    if (category === "All") {
      setTrainings(allTrainings);
    } else {
      // Filter locally - check if training's comma-separated category contains selected category
      const filtered = allTrainings.filter((t) =>
        trainingHasCategory(t, category),
      );
      console.log(
        "[TrainingList] Filtered trainings for",
        category,
        ":",
        filtered.length,
      );
      setTrainings(filtered);
    }
  };

  const getDifficultyDots = (level) => {
    const dots = [];
    for (let i = 1; i <= 5; i++) {
      dots.push(
        <span
          key={i}
          className={`difficulty-dot ${i <= level ? "active" : ""}`}
        />,
      );
    }
    return dots;
  };

  const getStatusBadge = (training) => {
    const status = training.userStatus || "NOT_STARTED";
    const statusConfig = {
      NOT_STARTED: { label: "Not Started", class: "not-started" },
      IN_PROGRESS: {
        label: `${training.userProgress || 0}% Complete`,
        class: "in-progress",
      },
      COMPLETED: { label: "Completed", class: "completed" },
    };

    const config = statusConfig[status] || statusConfig.NOT_STARTED;
    return (
      <span className={`training-status-badge ${config.class}`}>
        {config.label}
      </span>
    );
  };

  /**
   * Get relevance badge for hybrid training assignment model
   * Shows: Required (must complete), Recommended, or nothing for elective
   */
  const getRelevanceBadge = (training) => {
    const relevance = training.relevanceType;
    if (!relevance || relevance === "elective") return null;

    const relevanceConfig = {
      required: {
        label: "Required",
        class: "relevance-required",
      },
      recommended: {
        label: "Recommended",
        class: "relevance-recommended",
      },
      above_level: {
        label: "Can Test Out",
        class: "relevance-testout",
      },
    };

    const config = relevanceConfig[relevance];
    if (!config) return null;

    return (
      <span className={`training-relevance-badge ${config.class}`}>
        {config.label}
      </span>
    );
  };

  /**
   * Get the primary category to display on the card
   * (first category in the comma-separated list, formatted for display)
   */
  const getPrimaryCategory = (categoryString) => {
    if (!categoryString) return "General";
    const categories = categoryString.split(",").map((c) => c.trim());
    const primaryCategory = categories[0] || "General";
    return formatCategoryForDisplay(primaryCategory);
  };

  if (loading) {
    return (
      <div className="training-container">
        <LoadingSpinner message="Loading trainings..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="training-container">
        <div className="training-error">
          <div className="training-error-icon">!</div>
          <p className="training-error-message">{error}</p>
          <button className="btn btn-primary" onClick={fetchData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Get trainings filtered by both category and status
  const statusCounts = getStatusCounts(trainings);
  const displayedTrainings = filterByStatus(trainings, statusTab);

  return (
    <div className="training-container">
      {/* Status Tabs - Continue Learning vs Completed */}
      <div className="training-status-tabs">
        <button
          className={`training-status-tab ${statusTab === "continue" ? "active" : ""}`}
          onClick={() => setStatusTab("continue")}
        >
          Continue Learning
          {statusCounts.continue > 0 && (
            <span className="status-tab-count">{statusCounts.continue}</span>
          )}
        </button>
        <button
          className={`training-status-tab ${statusTab === "completed" ? "active" : ""}`}
          onClick={() => setStatusTab("completed")}
        >
          Completed
          {statusCounts.completed > 0 && (
            <span className="status-tab-count completed">
              {statusCounts.completed}
            </span>
          )}
        </button>
      </div>

      {/* Category Filter Dropdown */}
      <div className="training-filter-container">
        <label htmlFor="category-filter" className="training-filter-label">
          Filter by Category:
        </label>
        <select
          id="category-filter"
          value={selectedCategory}
          onChange={(e) => handleCategoryChange(e.target.value)}
          className="training-category-select"
        >
          {TRAINING_CATEGORIES.map((category) => {
            const count = categoryCounts[category] || 0;
            return (
              <option key={category} value={category}>
                {category} ({count})
              </option>
            );
          })}
        </select>

        {/* Results Summary */}
        <span className="training-results-summary">
          Showing {displayedTrainings.length}{" "}
          {statusTab === "completed" ? "completed" : "active"} trainings
          {selectedCategory !== "All" && ` in "${selectedCategory}"`}
        </span>
      </div>

      {/* Training Grid */}
      {displayedTrainings.length === 0 ? (
        <div className="training-empty">
          <div className="training-empty-icon">?</div>
          <p className="training-empty-message">
            {statusTab === "completed"
              ? "No completed trainings yet. Start learning to earn badges!"
              : selectedCategory !== "All"
                ? `No active trainings in the "${selectedCategory}" category.`
                : "All caught up! Check the Completed tab to review past trainings."}
          </p>
          {statusTab === "completed" && (
            <button
              className="btn btn-primary"
              onClick={() => setStatusTab("continue")}
            >
              Start Learning
            </button>
          )}
          {statusTab === "continue" && selectedCategory !== "All" && (
            <button
              className="btn btn-ghost"
              onClick={() => handleCategoryChange("All")}
            >
              Show All Categories
            </button>
          )}
        </div>
      ) : (
        <div className="training-grid">
          {displayedTrainings.map((training) => (
            <div
              key={training.trainingId}
              className={`training-card ${training.isFeatured ? "featured" : ""} ${training.relevanceType === "required" ? "required" : ""}`}
            >
              <div className="training-card-header">
                <span className="training-card-category">
                  {getPrimaryCategory(training.category)}
                </span>
                <div className="training-card-difficulty">
                  {getDifficultyDots(training.difficultyLevel || 1)}
                </div>
              </div>

              {/* Relevance Badge - shows if training is required or recommended for user */}
              {getRelevanceBadge(training)}

              <h3 className="training-card-title">{training.title}</h3>

              <p className="training-card-description">
                {training.summary || training.description?.substring(0, 150)}
                {training.description?.length > 150 && "..."}
              </p>

              <div className="training-card-meta">
                <span className="training-card-meta-item">
                  {training.estimatedMinutes || training.duration || "10"} min
                </span>
                {training.hasQuiz && (
                  <span className="training-card-meta-item">Quiz included</span>
                )}
              </div>

              {training.userStatus === "IN_PROGRESS" && (
                <div className="training-card-progress">
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${training.userProgress || 0}%` }}
                    />
                  </div>
                  <div className="progress-bar-label">
                    <span>{training.userProgress || 0}% complete</span>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: "16px" }}>
                {getStatusBadge(training)}
                {training.isTestedOut && (
                  <span className="training-tested-out-badge">Tested Out</span>
                )}
              </div>

              <div className="training-card-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => onSelectTraining && onSelectTraining(training)}
                >
                  {training.userStatus === "IN_PROGRESS"
                    ? "Continue"
                    : training.userStatus === "COMPLETED"
                      ? "Review"
                      : training.canTestOut &&
                          training.relevanceType === "above_level"
                        ? "Test Out"
                        : "Start Training"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrainingList;
